import React, { useMemo } from 'react';
import { ThemeContext, ThemeContextValues } from './ThemeContext';
import { HoneycombStyleSystemProps, HoneycombThemeOverrides } from '../types';
import { CssReset } from '../../components/CssReset/CssReset';
import { useHoneycombTheme } from '../hooks/useHoneycombTheme';
import { createTheme, mergeThemeOverrides } from '../createTheme/createTheme';
import { GlobalVariables } from './GlobalVariables';
import { GlobalStyles } from './GlobalStyles/GlobalStyles';

export type ThemeProviderProps = {
    /**
     * The child components.
     */
    children: React.ReactNode;

    /**
     * The theme overrides. These will be merged with the parent theme (usually the default theme).
     */
    theme?: HoneycombThemeOverrides;

    /**
     * An optional object composed of one or more key-value pairs, where each key is a string and each value is
     * an object of style system props. This can be used to augment any nested component based on `Styler`
     * simply by adding a `StyleId` prop equal to the object key.
     */
    customStyleProps?: {
        [key: string]: HoneycombStyleSystemProps;
    };

    /**
     * If `true`, global CSS reset styles will be applied.
     * @default true
     */
    withCSSReset?: boolean;
};

export function HoneycombThemeProvider(props: ThemeProviderProps): React.JSX.Element {
    const { children, theme: themeOverrides = {}, withCSSReset = true, customStyleProps: customStylePropsProp } = props;

    const { theme: outerTheme, isDefaultTheme, customStyleProps: outerCustomStyleProps } = useHoneycombTheme();

    const theme = isDefaultTheme ? createTheme(themeOverrides) : mergeThemeOverrides(outerTheme, themeOverrides);

    const context: ThemeContextValues = useMemo(() => {
        return {
            theme,
            isDefaultTheme: false,
            customStyleProps: { ...outerCustomStyleProps, ...customStylePropsProp },
        };
    }, [theme, outerCustomStyleProps, customStylePropsProp]);

    return (
        <ThemeContext.Provider value={context}>
            {withCSSReset && <CssReset />}
            <GlobalVariables />
            <GlobalStyles />
            {children}
        </ThemeContext.Provider>
    );
}
